import * as Sentry from '@sentry/react';
import packageJson from '../../package.json';
import { getConfigValue } from '../config/clients';
import {
  isChunkLoadError,
  collectChunkDiagnostics,
  installChunkErrorListeners,
} from './chunkDiagnostics';

const isDevelopment = process.env.NODE_ENV === 'development';
const toUrlMatcher = (value) => (value.startsWith('^') ? new RegExp(value) : value);

// Turned off for development to reduce noise
if (process.env.NODE_ENV === 'production') {
  const isCanary = document.cookie.includes('msales_version=canary');
  const environment = isDevelopment
    ? 'development'
    : isCanary
    ? 'canary'
    : 'production';
  const isMobile = /mobile|android|ios|iphone/i.test(navigator.userAgent);

  const tracePropagationTargets = getConfigValue(
    'monitoring.tracePropagationTargets',
    ['localhost'],
  ).map(toUrlMatcher);

  const errorTypeMap = {
    GraphQL: 'graphql',
    Apollo: 'apollo',
    Firebase: 'firebase',
    LocalForage: 'storage',
    IndexedDB: 'storage',
  };

  const appStartedAt = Date.now();
  const sessionAgeBucket = () => {
    const min = (Date.now() - appStartedAt) / 60000;
    if (min < 5) return '<5m';
    if (min < 15) return '5-15m';
    if (min < 30) return '15-30m';
    if (min < 60) return '30-60m';
    if (min < 120) return '1-2h';
    if (min < 240) return '2-4h';
    if (min < 480) return '4-8h';
    return '8h+';
  };

  Sentry.init({
    dsn:
      process.env.REACT_APP_SENTRY_DSN ||
      'https://fda4c0f81310b83b0f455ee75e4ce0fe@sentry.infostudio.app/138',
    environment,

    release: `msales-app@${packageJson.version}`,

    sendDefaultPii: true,
    sampleRate: 1.0,
    tracesSampler: (samplingContext) => {
      if (isDevelopment) return 0;
      const op = samplingContext?.transactionContext?.op;
      return op === 'pageload' ? 1.0 : 0.1;
    },

    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['error', 'warn'],
      }),

      Sentry.browserTracingIntegration({
        tracePropagationTargets: tracePropagationTargets,
        enableInp: true,
      }),
    ],

    ignoreErrors: [
      'ne zadovoljava uvjet',
      'Nevažeći task-subjekt ID',
      'top.GLOBALS',
      'chrome-extension',
      'moz-extension',
      '__firefox__',
      'Network request failed',
      'NetworkError',
      'Failed to fetch',
      'Load failed',
      'fb_xd_fragment',
      "Can't find variable: ZiteReader",
      'jigsaw is not defined',
      'ComboSearch is not defined',
      'atomicFindClose',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'AbortError',
      'The operation was aborted',
      'QuotaExceededError',
      'Failed to update a ServiceWorker',
      /Invalid DOM property.*fill-rule.*fillRule/i,
      /Invalid DOM property.*stroke-width.*strokeWidth/i,
      /Invalid DOM property.*stroke-linecap.*strokeLinecap/i,
      /Invalid DOM property.*stroke-linejoin.*strokeLinejoin/i,
      /Invalid DOM property.*clip-path.*clipPath/i,
      /Invalid DOM property.*fill-opacity.*fillOpacity/i,
      /Invalid DOM property.*stroke-opacity.*strokeOpacity/i,
      /status code 400.*Firebase ID token/i,
      /status code 400.*Unauthorized/i,
      /status code 400.*auth/i,
      /Firebase ID token has expired/i,
      /Timeout after \d+ms/i,
      /IntervalCoordinator.*Timeout/i,
      // Network/server errors (nginx 502, server returning HTML, etc.)
      /Unexpected token '<'/i,
      /ServerParseError/i,
      /Received status code 400/i,
      /Received status code 502/i,
      /Received status code 503/i,
      /Received status code 504/i,
      /ServerError.*status code 4\d\d/i,
      /ServerError.*status code 5\d\d/i,
      /Network error.*status code/i,
      /A network error.*such as timeout/i,
      /quota.*exceeded/i,
      /\[HealthCheck\] Server did not respond/i,
      /\[HealthCheck\] Server responded with status/i,
      /Server health check failed/i,
      /GraphQL server unreachable/i,
      /Event saved locally due to offline status/i,
      /\[DeviceRegistry\] Touch failed/i,
      /Sync Diagnostics/i,
      /\[IntervalCoordinator\].+took .+limit/i,
      /\[ImageUpdater\] Cannot run UPDATE mode/i,
      /\[CacheBuster\] Failed to check version/i,
      /\[Draft Monitor\] No current user/i,
      /Manual Sync Attempt/i,
      /Token timeout/i,
      /Sync mutation timeout/i,
      /Deprecation warning: value provided is not in a recognized/i,
    ],

    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === 'console' && breadcrumb.level !== 'error') {
        return null;
      }

      if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
        if (breadcrumb.data?.url?.includes('hooks.slack.com')) {
          return null;
        }

        if (breadcrumb.data?.status_code && breadcrumb.data.status_code < 400) {
          return null;
        }
      }

      if (breadcrumb.category === 'navigation') {
        breadcrumb.data = breadcrumb.data || {};
        breadcrumb.data.timestamp = new Date().toISOString();
      }

      return breadcrumb;
    },

    async beforeSend(event, hint) {
      const errorMessage = event.exception?.values?.[0]?.value || '';

      if (
        errorMessage.includes('ServiceWorker registration failed') ||
        (errorMessage.includes('InvalidStateError') &&
          errorMessage.includes('indexedDB'))
      ) {
        return null;
      }

      // rc-drawer swipe-to-close handler throws on multi-touch when startPos is
      // cleared. Unhandled but harmless (drawer still closes via mask/button); it
      // only spams Sentry. Match its unique method name, which survives minification.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
      if (
        frames.some(
          (f) =>
            typeof f.function === 'string' &&
            f.function.includes('removeMoveHandler'),
        )
      ) {
        return null;
      }

      if (
        errorMessage.includes('Invalid DOM property') &&
        (errorMessage.includes('fill-rule') ||
          errorMessage.includes('stroke-width') ||
          errorMessage.includes('stroke-linecap') ||
          errorMessage.includes('clip-path'))
      ) {
        return null;
      }

      if (
        errorMessage.includes('status code 400') ||
        errorMessage.includes('statusCode 400')
      ) {
        if (
          errorMessage.includes('Firebase ID token') ||
          errorMessage.includes('Unauthorized') ||
          errorMessage.includes('Authentication failed') ||
          errorMessage.includes('auth')
        ) {
          return null;
        }
      }

      event.tags = event.tags || {};

      for (const [keyword, type] of Object.entries(errorTypeMap)) {
        if (errorMessage.includes(keyword)) {
          event.tags.errorType = type;
          break;
        }
      }

      event.tags.deployment = isCanary ? 'canary' : 'stable';
      event.tags.url = window.location.hostname;
      event.tags.deviceType = isMobile ? 'mobile' : 'desktop';
      event.tags.sessionAgeBucket = sessionAgeBucket();
      event.tags.sessionAgeMinutes = Math.round(
        (Date.now() - appStartedAt) / 60000,
      ).toString();

      const chunkErr = (hint && hint.originalException) || {
        message: errorMessage || event.message || '',
      };
      if (isChunkLoadError(chunkErr)) {
        try {
          const diag = await collectChunkDiagnostics(chunkErr);
          event.tags = { ...event.tags, ...diag.tags };
          event.contexts = { ...event.contexts, chunkDiagnostics: diag.context };
        } catch (e) {
          // never block send on diagnostics
        }
      }

      return event;
    },

    beforeSendTransaction(event) {
      try {
        event.tags = event.tags || {};
        event.tags.deployment = isCanary ? 'canary' : 'stable';
        event.tags.url = window.location.hostname;
        event.tags.deviceType = isMobile ? 'mobile' : 'desktop';
        event.tags.sessionAgeBucket = sessionAgeBucket();
        event.tags.sessionAgeMinutes = Math.round(
          (Date.now() - appStartedAt) / 60000,
        ).toString();

        event.measurements = event.measurements || {};

        if (
          event.contexts?.trace?.op === 'pageload' &&
          typeof window.__msalesBootToContentMs === 'number'
        ) {
          event.measurements.boot_to_content_ms = {
            value: window.__msalesBootToContentMs,
            unit: 'millisecond',
          };
        }

        if (typeof performance !== 'undefined' && performance.memory) {
          const m = performance.memory;
          event.measurements.memory_used = {
            value: m.usedJSHeapSize,
            unit: 'byte',
          };
          event.measurements.memory_total = {
            value: m.totalJSHeapSize,
            unit: 'byte',
          };
          event.measurements.memory_limit = {
            value: m.jsHeapSizeLimit,
            unit: 'byte',
          };
          event.measurements.memory_percent = {
            value: (m.usedJSHeapSize / m.jsHeapSizeLimit) * 100,
            unit: 'percent',
          };
        }

        if (typeof document !== 'undefined') {
          event.measurements.dom_nodes = {
            value: document.getElementsByTagName('*').length,
            unit: 'none',
          };
          event.measurements.blob_url_count = {
            value: document.querySelectorAll('img[src^="blob:"]').length,
            unit: 'none',
          };
        }

        const apolloCache = window.__apolloClient?.cache;
        if (apolloCache) {
          const keys = Object.keys(apolloCache.extract());
          event.measurements.apollo_cache_keys = {
            value: keys.length,
            unit: 'none',
          };

          const byTypename = {};
          for (const k of keys) {
            const stripped = k.startsWith('$') ? k.slice(1) : k;
            const t = stripped.split(/[:.]/, 1)[0] || 'OTHER';
            byTypename[t] = (byTypename[t] || 0) + 1;
          }
          Object.entries(byTypename)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
            .forEach(([typename, count]) => {
              const safe = typename
                .replace(/[^A-Za-z0-9_]/g, '_')
                .toLowerCase();
              event.measurements[`apollo_cache_${safe}`] = {
                value: count,
                unit: 'none',
              };
            });
        }

        const intervalStats = window.intervalCoordinator?.getStats?.();
        if (intervalStats) {
          event.measurements.active_intervals = {
            value: intervalStats.activeIntervals || 0,
            unit: 'none',
          };
        }
      } catch (e) {
        // never block transaction send on instrumentation error
      }

      return event;
    },

    debug: false,
  });

  installChunkErrorListeners();

  window.sentryTrackMetric = (metricName, value, unit = 'millisecond') => {
    const transaction = Sentry.getCurrentScope().getTransaction();
    if (transaction) {
      transaction.setMeasurement(metricName, value, unit);
    }
  };

  window.sentryTrackAction = (actionName, data = {}) => {
    Sentry.addBreadcrumb({
      category: 'user-action',
      message: actionName,
      level: 'info',
      data,
    });
  };

  const toIso = (raw) => {
    const ms = Number(raw);
    return Number.isFinite(ms) ? new Date(ms).toISOString() : 'unknown';
  };
  try {
    const fallbackAt = localStorage.getItem('msales_boot_fallback_at');
    if (fallbackAt) {
      localStorage.removeItem('msales_boot_fallback_at');
      Sentry.captureMessage('[BootWatchdog] Fallback shown on previous load', {
        level: 'warning',
        tags: { event: 'boot-watchdog-fired' },
        extra: { firedAt: toIso(fallbackAt) },
      });
    }
    const resetAt = localStorage.getItem('msales_boot_reset_at');
    if (resetAt) {
      localStorage.removeItem('msales_boot_reset_at');
      Sentry.captureMessage('[BootWatchdog] User clicked reset on previous load', {
        level: 'info',
        tags: { event: 'boot-watchdog-reset' },
        extra: { clickedAt: toIso(resetAt) },
      });
    }
  } catch (e) {
    // ignore
  }
}
